import {Plural, Trans, t} from '@lingui/macro';
import {createTRPCReact} from '@trpc/react-query';
import type {ServerRouter} from '@zentact/api';
import {useNotification} from '@zentact/ui-tailwind';
import {AlertBanner} from '@zentact/ui-tailwind/elements/AlertBanner';
import {DateTime} from 'luxon';
import {useCallback} from 'react';

const calculateDaysToRespond = (expirationDate: DateTime) =>
  Math.floor(Math.abs(DateTime.now().diff(expirationDate, 'days').days));

interface MerchantReviewDetailsBannerProps {
  merchantAccountInPeriodicReviewDate: string;
  merchantAccountId: string;
  trpc: ReturnType<typeof createTRPCReact<ServerRouter>>;
}
export const MerchantReviewDetailsBanner = ({
  merchantAccountInPeriodicReviewDate,
  merchantAccountId,
  trpc,
}: MerchantReviewDetailsBannerProps) => {
  const expirationDate = DateTime.fromISO(merchantAccountInPeriodicReviewDate);
  const daysToRespond = calculateDaysToRespond(expirationDate);
  const {showErrorNotification} = useNotification();
  const trpcContext = trpc.useUtils();

  const handleOpenHop = useCallback(async () => {
    try {
      const {redirectUrl} = await trpcContext.merchantRegistration.getResumeHopLink.fetch({
        merchantAccountId,
      });
      window.open(redirectUrl, '_blank');
    } catch (e) {
      showErrorNotification(t`Failed to generate a hop link`, (e as Error).message);
    }
  }, [merchantAccountId, showErrorNotification]);

  return (
    <AlertBanner
      alertTitle={
        <Trans>
          Review Required in{' '}
          {daysToRespond === 0 ? (
            'less than a day'
          ) : (
            <>
              {daysToRespond} <Plural value={daysToRespond} one="day" other="days" />
            </>
          )}
        </Trans>
      }
      alertMessage={
        <Trans>Review Application before deadline or merchant might not be able to process</Trans>
      }
      alertType="warning"
      rightButtonLabel={<Trans>Start review</Trans>}
      rightButtonAction={handleOpenHop}
    />
  );
};
